import { createSelector } from '@reduxjs/toolkit';

import { isolatedDialog, isolatedHeader, type IsolatedKey } from 'Addons/fitmentSearch/isolatedKeys';
import { selectedVehicleSelector } from 'Core/reducers/fitmentSearch/storage';
import type { ReduxStateType, Selector } from 'Core/store';
import { type FacetCollection, FacetValue, Vehicle } from 'Models/index';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';
import requestConfig from 'Models/uiConfig/requestConfig';
import { createFacetCollectionSelector, createIsolatedSelectorCreator } from 'Modules/selectors';
import { equalBy } from 'Utils/array';

import { fitmentSearchSelector } from './fitmentSearch';

type FitmentSearchSlice = Pick<ReduxStateType['fitmentSearch'], 'fields' | 'request' | 'response'>;
type FitmentSearchIsolatedKey = Exclude<IsolatedKey, null>;

const emptyFields: string[] = [];

function isFitmentSearchIsolatedKey(key: string | null): key is FitmentSearchIsolatedKey {
  return key === isolatedDialog || key === isolatedHeader;
}

function createRootSelector(isolatedKey: string | null = null): Selector<FitmentSearchSlice> {
  return (state) => {
    const fitmentSearchState = fitmentSearchSelector(state);
    return isFitmentSearchIsolatedKey(isolatedKey) ? fitmentSearchState[isolatedKey] : fitmentSearchState;
  };
}

const createFitmentSearchBaseFieldsSelector = createIsolatedSelectorCreator(
  createRootSelector,
  ([rootSelector]) =>
    (state: ReduxStateType) => {
      return rootSelector(state).fields.base;
    },
);

export const createFitmentSearchExtraFieldsSelector = createIsolatedSelectorCreator(
  createRootSelector,
  ([rootSelector]) =>
    (state: ReduxStateType) =>
      rootSelector(state).fields.extra,
);

export const createFitmentSearchFieldsSelector = createIsolatedSelectorCreator(
  createFitmentSearchBaseFieldsSelector,
  createFitmentSearchExtraFieldsSelector,
  (selectors) => createSelector(selectors, (base: string[], extra: string[]) => [...base, ...extra]),
);

const createIsolatedRequestSelector = createIsolatedSelectorCreator(
  createRootSelector,
  ([rootSelector]) =>
    (state: ReduxStateType) =>
      rootSelector(state).request,
);

export const createFitmentSearchRequestSelectionSelector = createIsolatedSelectorCreator(
  createIsolatedRequestSelector,
  ([requestSelector]) =>
    (state: ReduxStateType) =>
      requestSelector(state).selection,
);

const createIsolatedResponseSelector = createIsolatedSelectorCreator(
  createRootSelector,
  ([rootSelector]) =>
    (state: ReduxStateType) =>
      rootSelector(state).response,
);

export const createFitmentSearchResponseAllFacetsSelector = createIsolatedSelectorCreator(
  createIsolatedResponseSelector,
  (selectors) => createFacetCollectionSelector(selectors, (response) => response.facets),
);

export const createFilteredFitmentSearchResponseFieldsSelector = createIsolatedSelectorCreator(
  createFitmentSearchResponseAllFacetsSelector,
  createFitmentSearchBaseFieldsSelector,
  createFitmentSearchExtraFieldsSelector,
  createFitmentSearchFieldsSelector,
  (selectors, isolatedKey: string | null) =>
    createSelector(
      selectors,
      (
        facets: FacetCollection,
        fitmentBaseFields: string[],
        fitmentExtraFields: string[],
        fitmentFields: string[],
      ) => {
        const allBaseFieldsSelected = fitmentBaseFields.every((field) => {
          const facet = facets.get(field);
          return !facet || facet.selection?.length;
        });
        return facets
          .filter((facet) => {
            // do not remove any base field
            if (fitmentBaseFields.includes(facet.field)) {
              return true;
            }

            // do not remove any extra field if there is only one base field
            if (
              fitmentBaseFields.length === 1 &&
              fitmentExtraFields.length &&
              fitmentFields.includes(facet.field)
            ) {
              return true;
            }

            // remove the extra field in the following cases
            if (
              (!fitmentSearchConfig.isExtraFieldsAlwaysVisible &&
                (fitmentSearchConfig.isOnCategorySelectionPage ||
                  isolatedKey === isolatedHeader ||
                  (isolatedKey === isolatedDialog && !requestConfig.hasSearchResults))) ||
              !allBaseFieldsSelected ||
              !fitmentFields.includes(facet.field)
            ) {
              return false;
            }

            // do not remove selected extra fields
            if (facet.selection.length) {
              return true;
            }

            // do not remove extra fields if:
            // partial mode enabled
            // or in VerifyFitment widget
            // or if there is values and not only one Universal value
            return fitmentSearchConfig.isVehicleWidgetInPartialMode || facet.values.length > 0;
          })
          .map((facet) => facet.field);
      },
    ),
);

export const createRequiredFitmentSearchFieldsSelector = createIsolatedSelectorCreator(
  createFilteredFitmentSearchResponseFieldsSelector,
  createFitmentSearchBaseFieldsSelector,
  (selectors) =>
    createSelector(selectors, (responseFields: string[], baseFields: string[]) => {
      const requiredFields = [baseFields, responseFields].reduce((requiredFields: string[], fitmentFields) =>
        requiredFields.filter((requiredField) => fitmentFields.includes(requiredField)),
      );
      return requiredFields.length ? requiredFields : null;
    }),
);

export const createFitmentSearchResponseFacetsSelector = createIsolatedSelectorCreator(
  createFitmentSearchResponseAllFacetsSelector,
  createFilteredFitmentSearchResponseFieldsSelector,
  (selectors) =>
    createFacetCollectionSelector(selectors, (facets: FacetCollection, fields: string[]) =>
      facets.filter((f) => fields.includes(f.field)),
    ),
);

export const createFitmentSearchResponseSelectionSelector = createIsolatedSelectorCreator(
  createFitmentSearchResponseFacetsSelector,
  (selectors) => createSelector(selectors, (facets) => facets.selection),
);

export const createFitmentSearchSelectionChangedSelector = createIsolatedSelectorCreator(
  createFitmentSearchRequestSelectionSelector,
  createFitmentSearchResponseSelectionSelector,
  (selectors) =>
    createSelector(
      selectors,
      (requestSelection, responseSelection) =>
        !equalBy(requestSelection, responseSelection, FacetValue.termKey),
    ),
);

export const createAllBaseFieldsHaveSelectionSelector = createIsolatedSelectorCreator(
  createFitmentSearchBaseFieldsSelector,
  createFitmentSearchResponseSelectionSelector,
  ([baseFieldsSelector, selectionSelector]) =>
    (state: ReduxStateType) => {
      const baseFields = baseFieldsSelector(state);
      const selection = selectionSelector(state);
      const selectedFields = selection.map((sel) => sel.field);
      return !(baseFields && baseFields.filter((field) => !selectedFields.includes(field)).length);
    },
);

export const createAllRequiredFieldsHaveSelectionSelector = createIsolatedSelectorCreator(
  createRequiredFitmentSearchFieldsSelector,
  createFitmentSearchResponseSelectionSelector,
  ([requiredFieldsSelector, selectionSelector]) =>
    (state: ReduxStateType) => {
      const requiredFields = requiredFieldsSelector(state);
      const selection = selectionSelector(state);
      const selectedFields = selection.map((sel) => sel.field);
      return !(requiredFields && requiredFields.filter((field) => !selectedFields.includes(field)).length);
    },
);

export const createResponseVehicleSelector = createIsolatedSelectorCreator(
  createFitmentSearchResponseSelectionSelector,
  createFitmentSearchFieldsSelector,
  (selectors) =>
    createSelector(selectors, (selection, fitmentFields) => new Vehicle(selection, fitmentFields)),
);

export const createIsServerChangedVehicleSelector = createIsolatedSelectorCreator(
  createResponseVehicleSelector,
  createAllRequiredFieldsHaveSelectionSelector,
  createFitmentSearchFieldsSelector,
  ([responseVehicleSelector, isAllRequiredFieldHasSelectionSelector, fitmentFieldsSelector]) =>
    createSelector(
      [
        selectedVehicleSelector,
        responseVehicleSelector,
        isAllRequiredFieldHasSelectionSelector,
        fitmentFieldsSelector,
        (_state: ReduxStateType, fields?: string[]) => fields ?? emptyFields,
      ],
      (
        selectedVehicle: Vehicle,
        responseVehicle: Vehicle,
        isAllRequiredFieldHasSelection: boolean,
        allFields: string[],
        widgetFields: string[],
      ) => {
        const fitmentFields = widgetFields?.length ? widgetFields : allFields;
        const selectedFilteredVehicle = new Vehicle(selectedVehicle.fieldMap, fitmentFields);
        const responseFilteredVehicle = new Vehicle(responseVehicle.fieldMap, fitmentFields);

        return (
          selectedFilteredVehicle.notNull &&
          !selectedFilteredVehicle.equals(responseFilteredVehicle) &&
          (!selectedFilteredVehicle.isMoreSpecificThan(responseFilteredVehicle) ||
            !isAllRequiredFieldHasSelection)
        );
      },
    ),
);
